import { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, MousePointer2, Plus, Redo2, Trash2, Undo2, X } from 'lucide-react';
import { COURT_WIDTH, isLine, type Diagram, type DiagramObject, type DiagramStep, type LineKind } from '@hoopify/shared';
import { newId } from '../../lib/plan';
import { Button, Dialog } from '../ui';
import { CourtLines, DiagramObjects, courtLength, viewBox } from './Court';

type PlaceKind = 'offence' | 'defence' | 'ball' | 'cone' | 'coach' | 'text';
type Tool = 'select' | PlaceKind | LineKind;
type Pt = { x: number; y: number };

const blankStep = (): DiagramStep => ({ id: newId(), caption: null, objects: [] });
export const blankDiagram = (): Diagram => ({ court: 'half', steps: [blankStep()] });

const TOOLS: { id: Tool; label: string; hint: string }[] = [
  { id: 'select', label: 'Select and move', hint: 'Click something to select it, then drag to move it. Press Delete to remove it.' },
  { id: 'offence', label: 'Offence player', hint: 'Click the court to place an attacking player. Numbers go up automatically.' },
  { id: 'defence', label: 'Defender', hint: 'Click the court to place a defender.' },
  { id: 'ball', label: 'Ball', hint: 'Click the court to place a ball.' },
  { id: 'cone', label: 'Cone', hint: 'Click the court to place a cone.' },
  { id: 'coach', label: 'Coach', hint: 'Click the court to place a coach.' },
  { id: 'text', label: 'Label', hint: 'Click the court to add a short label, then type it above.' },
  { id: 'cut', label: 'Cut or move', hint: 'Click where the move starts, click to bend it, and double-click (or press Finish) to end.' },
  { id: 'pass', label: 'Pass', hint: 'Click where the pass starts, then double-click where it’s caught.' },
  { id: 'dribble', label: 'Dribble', hint: 'Click where the dribble starts, click to bend it, and double-click to end.' },
  { id: 'screen', label: 'Screen', hint: 'Click where the screener starts, then double-click where they set the screen.' },
];

function ToolIcon({ tool }: { tool: Tool }) {
  const common = { width: 20, height: 20, viewBox: '0 0 20 20', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  switch (tool) {
    case 'select':
      return <MousePointer2 className="size-5" aria-hidden />;
    case 'offence':
      return (
        <svg {...common}>
          <circle cx="10" cy="10" r="7" />
          <text x="10" y="13.5" textAnchor="middle" fontSize="9" fontWeight="700" fill="currentColor" stroke="none">1</text>
        </svg>
      );
    case 'defence':
      return (
        <svg {...common}>
          <path d="M5 5 L15 15 M15 5 L5 15" strokeWidth={2} />
        </svg>
      );
    case 'ball':
      return (
        <svg {...common}>
          <circle cx="10" cy="10" r="5" fill="#e2742b" stroke="#9a4612" />
        </svg>
      );
    case 'cone':
      return (
        <svg {...common}>
          <path d="M10 4 L15 15 L5 15 Z" fill="#f2a516" stroke="#a86a00" />
        </svg>
      );
    case 'coach':
      return (
        <svg {...common}>
          <rect x="4" y="4" width="12" height="12" rx="2" />
          <text x="10" y="13.5" textAnchor="middle" fontSize="9" fontWeight="700" fill="currentColor" stroke="none">C</text>
        </svg>
      );
    case 'text':
      return (
        <svg {...common}>
          <path d="M5 5 H15 M10 5 V16" strokeWidth={2} />
        </svg>
      );
    case 'cut':
      return (
        <svg {...common}>
          <path d="M3 16 Q 6 4 15 6" />
          <path d="M12 3.5 L15.5 6 L12.5 9" />
        </svg>
      );
    case 'pass':
      return (
        <svg {...common}>
          <path d="M3 15 L15 5" strokeDasharray="2.5 2" />
          <path d="M11 4.5 L15.5 4.5 L15.5 9" />
        </svg>
      );
    case 'dribble':
      return (
        <svg {...common}>
          <path d="M2 14 L4 11 L6 14 L8 11 L10 14 L12 11 L15 11" />
          <path d="M13 8.5 L16 11 L13 13.5" />
        </svg>
      );
    case 'screen':
      return (
        <svg {...common}>
          <path d="M4 16 L13 7" />
          <path d="M10 4 L16 10" strokeWidth={2.2} />
        </svg>
      );
  }
}

/** Full-screen-ish dialog for drawing a drill's diagram. Changes apply only on Done. */
export function DiagramEditorDialog({ open, value, onSave, onClose, title = 'Court diagram' }: { open: boolean; value: Diagram | null; onSave: (d: Diagram | null) => void; onClose: () => void; title?: string }) {
  return (
    <Dialog open={open} onClose={onClose} title={title} size="xl">
      {open && <DiagramEditor initial={value} onSave={onSave} onCancel={onClose} />}
    </Dialog>
  );
}

function DiagramEditor({ initial, onSave, onCancel }: { initial: Diagram | null; onSave: (d: Diagram | null) => void; onCancel: () => void }) {
  const [d, setD] = useState<Diagram>(() => structuredClone(initial ?? blankDiagram()));
  const [past, setPast] = useState<Diagram[]>([]);
  const [future, setFuture] = useState<Diagram[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [tool, setTool] = useState<Tool>(initial ? 'select' : 'offence');
  const [selected, setSelected] = useState<string | null>(null);
  const [drawing, setDrawing] = useState<{ kind: LineKind; points: Pt[] } | null>(null);
  const [hover, setHover] = useState<Pt | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<{ id: string; pointIndex: number | null; start: Pt; before: Diagram; moved: boolean } | null>(null);

  const step = d.steps[stepIndex]!;
  const length = courtLength(d.court);
  const selectedObj = step.objects.find((o) => o.id === selected) ?? null;

  const commit = useCallback(
    (next: Diagram) => {
      setPast((p) => [...p.slice(-60), d]);
      setFuture([]);
      setD(next);
    },
    [d],
  );
  const withObjects = (dd: Diagram, fn: (objs: DiagramObject[]) => DiagramObject[]): Diagram => ({
    ...dd,
    steps: dd.steps.map((s, i) => (i === stepIndex ? { ...s, objects: fn(s.objects) } : s)),
  });

  const undo = () => {
    const prev = past[past.length - 1];
    if (!prev) return;
    setFuture((f) => [d, ...f]);
    setPast((p) => p.slice(0, -1));
    setD(prev);
    setSelected(null);
    setStepIndex((i) => Math.min(i, prev.steps.length - 1));
  };
  const redo = () => {
    const next = future[0];
    if (!next) return;
    setPast((p) => [...p, d]);
    setFuture((f) => f.slice(1));
    setD(next);
  };

  const toCourt = (e: { clientX: number; clientY: number }): Pt => {
    const svg = svgRef.current!;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse());
    return { x: clamp(p.x, 0, COURT_WIDTH), y: clamp(p.y, 0, length) };
  };

  const finishLine = useCallback(() => {
    if (!drawing) return;
    const pts = dedupe(drawing.points);
    if (pts.length >= 2) {
      const obj: DiagramObject = { id: newId(), kind: drawing.kind, points: pts.map(round) };
      commit(withObjects(d, (o) => [...o, obj]));
    }
    setDrawing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawing, d, commit, stepIndex]);

  const nextLabel = (kind: 'offence' | 'defence') => {
    const used = new Set(step.objects.filter((o) => o.kind === kind).map((o) => (o as { label: string }).label));
    let n = 1;
    while (used.has(String(n))) n++;
    return String(n);
  };

  // ---------- Pointer handling ----------

  const onCourtPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const p = round(toCourt(e));
    if (tool === 'select') {
      setSelected(null);
      return;
    }
    if (tool === 'cut' || tool === 'pass' || tool === 'dribble' || tool === 'screen') {
      setDrawing((cur) => (cur && cur.kind === tool ? { ...cur, points: [...cur.points, p].slice(0, 8) } : { kind: tool, points: [p] }));
      return;
    }
    let obj: DiagramObject;
    const id = newId();
    if (tool === 'offence' || tool === 'defence') obj = { id, kind: tool, x: p.x, y: p.y, label: nextLabel(tool) };
    else if (tool === 'text') obj = { id, kind: 'text', x: p.x, y: p.y, text: 'Label' };
    else obj = { id, kind: tool, x: p.x, y: p.y };
    commit(withObjects(d, (o) => [...o, obj]));
    if (tool === 'text') {
      setSelected(id);
      setTool('select');
    }
  };

  const onObjectPointerDown = (id: string, e: React.PointerEvent) => {
    if (tool !== 'select') return;
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setSelected(id);
    drag.current = { id, pointIndex: null, start: toCourt(e), before: d, moved: false };
  };
  const onHandlePointerDown = (id: string, pointIndex: number, e: React.PointerEvent) => {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drag.current = { id, pointIndex, start: toCourt(e), before: d, moved: false };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const p = toCourt(e);
    if (drawing) setHover(p);
    const g = drag.current;
    if (!g) return;
    const dx = p.x - g.start.x;
    const dy = p.y - g.start.y;
    if (!g.moved && Math.hypot(dx, dy) < 0.05) return;
    g.moved = true;
    const src = g.before.steps[stepIndex]!.objects.find((o) => o.id === g.id);
    if (!src) return;
    let moved: DiagramObject;
    if (isLine(src)) {
      moved = {
        ...src,
        points: src.points.map((pt, i) =>
          g.pointIndex === null || g.pointIndex === i ? round({ x: clamp(pt.x + dx, 0, COURT_WIDTH), y: clamp(pt.y + dy, 0, length) }) : pt,
        ),
      };
    } else {
      moved = { ...src, ...round({ x: clamp(src.x + dx, 0, COURT_WIDTH), y: clamp(src.y + dy, 0, length) }) };
    }
    setD(withObjects(g.before, (objs) => objs.map((o) => (o.id === g.id ? moved : o))));
  };
  const onPointerUp = () => {
    const g = drag.current;
    drag.current = null;
    if (g?.moved) {
      setPast((p) => [...p.slice(-60), g.before]);
      setFuture([]);
    }
  };

  // ---------- Keyboard ----------

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement).closest('input, textarea, select');
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !typing) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y' && !typing) {
        e.preventDefault();
        redo();
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selected && !typing) {
        e.preventDefault();
        removeSelected();
      } else if (e.key === 'Enter' && drawing && !typing) {
        e.preventDefault();
        finishLine();
      } else if (e.key === 'Escape' && (drawing || selected)) {
        // Keep the dialog open: Escape first cancels what you're doing.
        e.preventDefault();
        e.stopPropagation();
        setDrawing(null);
        setSelected(null);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  const removeSelected = () => {
    if (!selected) return;
    commit(withObjects(d, (o) => o.filter((x) => x.id !== selected)));
    setSelected(null);
  };
  const updateSelected = (changes: Partial<DiagramObject>) => {
    if (!selected) return;
    commit(withObjects(d, (o) => o.map((x) => (x.id === selected ? ({ ...x, ...changes } as DiagramObject) : x))));
  };

  const pickTool = (t: Tool) => {
    if (drawing) finishLine();
    setTool(t);
    setSelected(null);
  };

  // ---------- Steps and court ----------

  const addStep = () => {
    // The next step starts with everyone where they were; lines are cleared.
    const carried = step.objects.filter((o) => !isLine(o)).map((o) => ({ ...o, id: newId() }));
    commit({ ...d, steps: [...d.steps, { id: newId(), caption: null, objects: carried }] });
    setStepIndex(d.steps.length);
    setSelected(null);
  };
  const removeStep = () => {
    if (d.steps.length < 2) return;
    commit({ ...d, steps: d.steps.filter((_, i) => i !== stepIndex) });
    setStepIndex(Math.max(0, stepIndex - 1));
    setSelected(null);
  };
  const setCourt = (court: 'half' | 'full') => {
    if (court === d.court) return;
    const max = courtLength(court);
    // Going to half court pulls anything past halfway back onto the court.
    commit({
      court,
      steps: d.steps.map((s) => ({
        ...s,
        objects: s.objects.map((o) => (isLine(o) ? { ...o, points: o.points.map((p) => ({ ...p, y: Math.min(p.y, max) })) } : { ...o, y: Math.min(o.y, max) })),
      })),
    });
  };

  const hint = drawing
    ? drawing.points.length < 2
      ? 'Click to add points. Double-click, press Enter or choose Finish to end the line.'
      : 'Double-click, press Enter or choose Finish to end the line. Escape cancels.'
    : TOOLS.find((t) => t.id === tool)!.hint;
  const preview = drawing && hover ? [...drawing.points, hover] : drawing?.points;

  return (
    <div
      className="flex flex-col gap-3 lg:flex-row"
      // The editor can sit inside another form (the drill editor); Enter in its fields mustn't submit that.
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT' && e.preventDefault()}
    >
      {/* Tools */}
      <div className="flex flex-wrap gap-1 lg:w-[156px] lg:flex-col lg:flex-nowrap" role="toolbar" aria-label="Drawing tools">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => pickTool(t.id)}
            aria-pressed={tool === t.id}
            title={t.label}
            className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] ${tool === t.id ? 'bg-action-soft text-action' : 'text-muted hover:bg-sunken hover:text-ink'}`}
          >
            <ToolIcon tool={t.id} />
            <span className="hidden lg:inline">{t.label}</span>
            <span className="sr-only lg:hidden">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="min-w-0 flex-1">
        {/* Top row: court, undo, steps */}
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <div className="grid grid-cols-2 gap-1 rounded-md bg-sunken p-1 text-[13px]" role="radiogroup" aria-label="Court">
            {(['half', 'full'] as const).map((c) => (
              <button key={c} type="button" role="radio" aria-checked={d.court === c} onClick={() => setCourt(c)} className={`rounded px-2.5 py-1 font-medium ${d.court === c ? 'bg-surface text-ink shadow-sm' : 'text-muted hover:text-ink'}`}>
                {c === 'half' ? 'Half court' : 'Full court'}
              </button>
            ))}
          </div>
          <button type="button" className="btn-ghost px-2" onClick={undo} disabled={!past.length} aria-label="Undo" title="Undo (Ctrl+Z)">
            <Undo2 className="size-4" />
          </button>
          <button type="button" className="btn-ghost px-2" onClick={redo} disabled={!future.length} aria-label="Redo" title="Redo (Ctrl+Y)">
            <Redo2 className="size-4" />
          </button>
          <div className="ml-auto flex items-center gap-1" role="tablist" aria-label="Steps">
            {d.steps.map((s, i) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={i === stepIndex}
                onClick={() => {
                  if (drawing) finishLine();
                  setStepIndex(i);
                  setSelected(null);
                }}
                className={`tnum rounded-md px-2.5 py-1 text-[13px] font-medium ${i === stepIndex ? 'bg-action text-action-ink' : 'bg-sunken text-muted hover:text-ink'}`}
              >
                Step {i + 1}
              </button>
            ))}
            {d.steps.length < 4 && (
              <button type="button" className="btn-ghost px-2 py-1 text-[13px]" onClick={addStep} title="Add a step that starts where this one ends">
                <Plus className="size-4" aria-hidden /> Step
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
          <div className="min-w-0">
            <svg
              ref={svgRef}
              viewBox={viewBox(d.court)}
              className={`mx-auto block w-full touch-none rounded-md select-none ${d.court === 'full' ? 'max-h-[70dvh] max-w-[380px]' : 'max-h-[62dvh] max-w-[560px]'} ${tool === 'select' ? '' : 'cursor-crosshair'}`}
              onPointerDown={onCourtPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={() => setHover(null)}
              onDoubleClick={(e) => {
                e.preventDefault();
                finishLine();
              }}
              role="application"
              aria-label="Court. Use the tools to place players and draw movement."
              style={{ fontFamily: 'system-ui, sans-serif' }}
            >
              <CourtLines court={d.court} />
              <DiagramObjects objects={step.objects} selectedId={selected} interactive={tool === 'select'} onObjectPointerDown={onObjectPointerDown} />
              {preview && preview.length > 0 && (
                <g pointerEvents="none">
                  <DiagramObjects objects={preview.length >= 2 ? [{ id: 'preview', kind: drawing!.kind, points: dedupe(preview).length >= 2 ? dedupe(preview) : preview }] : []} />
                  {drawing!.points.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r={0.14} fill="var(--action)" />
                  ))}
                </g>
              )}
              {selectedObj && isLine(selectedObj) &&
                selectedObj.points.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r={0.28} fill="var(--surface)" stroke="var(--action)" strokeWidth={0.07} style={{ cursor: 'grab' }} onPointerDown={(e) => onHandlePointerDown(selectedObj.id, i, e)} />
                ))}
            </svg>
            <p className="mt-2 text-[13px] text-muted" aria-live="polite">
              {hint}
            </p>
          </div>

          {/* Side panel: step caption and the selected thing */}
          <div className="space-y-3">
            <label className="block space-y-1">
              <span className="block text-[13px] font-medium">Step {stepIndex + 1} caption</span>
              <input
                className="field-input"
                maxLength={200}
                placeholder="e.g. 1 passes to 2 and cuts"
                value={step.caption ?? ''}
                onChange={(e) => setD({ ...d, steps: d.steps.map((s, i) => (i === stepIndex ? { ...s, caption: e.target.value || null } : s)) })}
              />
            </label>
            {drawing && (
              <div className="flex gap-2">
                <Button type="button" onClick={finishLine} disabled={dedupe(drawing.points).length < 2}>
                  Finish line
                </Button>
                <Button type="button" variant="secondary" onClick={() => setDrawing(null)}>
                  Cancel
                </Button>
              </div>
            )}
            {selectedObj && (
              <div className="space-y-2 rounded-md border border-line p-3">
                <p className="text-[13px] font-semibold">{describe(selectedObj)}</p>
                {(selectedObj.kind === 'offence' || selectedObj.kind === 'defence') && (
                  <label className="block space-y-1">
                    <span className="block text-[13px]">Number or letter</span>
                    <input className="field-input w-20" maxLength={3} value={selectedObj.label} onChange={(e) => updateSelected({ label: e.target.value })} />
                  </label>
                )}
                {selectedObj.kind === 'text' && (
                  <label className="block space-y-1">
                    <span className="block text-[13px]">Text</span>
                    <input className="field-input" maxLength={40} value={selectedObj.text} onChange={(e) => updateSelected({ text: e.target.value || ' ' })} autoFocus />
                  </label>
                )}
                {isLine(selectedObj) && (
                  <label className="block space-y-1">
                    <span className="block text-[13px]">Line type</span>
                    <select className="field-input py-1.5" value={selectedObj.kind} onChange={(e) => updateSelected({ kind: e.target.value as LineKind })}>
                      <option value="cut">Cut or move</option>
                      <option value="pass">Pass</option>
                      <option value="dribble">Dribble</option>
                      <option value="screen">Screen</option>
                    </select>
                    <span className="block text-[12px] text-muted">Drag the round handles to reshape it.</span>
                  </label>
                )}
                <Button type="button" variant="danger" onClick={removeSelected}>
                  <Trash2 className="size-4" aria-hidden /> Remove
                </Button>
              </div>
            )}
            <div className="flex flex-wrap gap-2 border-t border-line pt-3">
              <button type="button" className="btn-ghost px-2 text-[13px]" onClick={() => commit(withObjects(d, () => []))} disabled={!step.objects.length}>
                <X className="size-4" aria-hidden /> Clear step
              </button>
              {d.steps.length > 1 && (
                <button type="button" className="btn-ghost px-2 text-[13px]" onClick={removeStep}>
                  <Trash2 className="size-4" aria-hidden /> Remove step {stepIndex + 1}
                </button>
              )}
              {d.steps.length < 4 && (
                <button type="button" className="btn-ghost px-2 text-[13px]" onClick={addStep}>
                  <Copy className="size-4" aria-hidden /> Next step from here
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-line pt-4">
          {initial && (
            <Button type="button" variant="danger" className="btn-danger mr-auto" onClick={() => onSave(null)}>
              Remove diagram
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              const clean = { ...d, steps: d.steps.map((s) => ({ ...s, caption: s.caption?.trim() || null, objects: s.objects.map((o) => (o.kind === 'text' ? { ...o, text: o.text.trim() || 'Label' } : o)) })) };
              onSave(clean.steps.every((s) => !s.objects.length) ? null : clean);
            }}
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

function describe(o: DiagramObject) {
  switch (o.kind) {
    case 'offence':
      return `Player ${o.label}`;
    case 'defence':
      return `Defender X${o.label}`;
    case 'text':
      return 'Label';
    case 'ball':
      return 'Ball';
    case 'cone':
      return 'Cone';
    case 'coach':
      return 'Coach';
    default:
      return { cut: 'Cut or move', pass: 'Pass', dribble: 'Dribble', screen: 'Screen' }[o.kind];
  }
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}
function round(p: Pt): Pt {
  return { x: Math.round(p.x * 100) / 100, y: Math.round(p.y * 100) / 100 };
}
/** Drops points that sit on top of the previous one (double-clicks add two). */
function dedupe(points: Pt[]): Pt[] {
  return points.filter((p, i) => i === 0 || Math.hypot(p.x - points[i - 1]!.x, p.y - points[i - 1]!.y) > 0.25);
}
